import axios from 'axios';
import { createAsyncThunk, createSlice, isFulfilled, isPending, isRejected } from '@reduxjs/toolkit';

import { cleanEntity } from 'app/shared/util/entity-utils';
import { IQueryParams, createEntitySlice, EntityState, serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import { ISurveillant, defaultValue } from 'app/shared/model/surveillant.model';
import { schoolId } from 'app/sessionUtils';
import { identity } from 'lodash';
import { ISurveillantGeneral, defaultVal } from 'app/shared/model/surveillantGeneral';
const initialState = {
  loading: false,
  errorMessage: null,
  entities: [],
  entity: defaultValue,
  entitySG: defaultVal,
  updating: false,
  updateSuccess: false,
};

const apiUrl = 'api/surveillants';

// Actions

export const getSurveillants = createAsyncThunk('surveillant/fetch_surveillant_list', async (id: string | number) => {
  const requestUrl = `${apiUrl}?ecoleId=${id}`;
  return axios.get<ISurveillant[]>(requestUrl);
});

export const getSurveillant = createAsyncThunk(
  'surveillant/fetch_surveillant',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    return axios.get<ISurveillant>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const createSurveillant = createAsyncThunk(
  'surveillant/create_surveillant',
  async (entity: ISurveillant, thunkAPI) => {
    const result = await axios.post<ISurveillant>(`${apiUrl}/${entity.ecoleId}`, cleanEntity(entity));
    thunkAPI.dispatch(getSurveillants(entity.ecoleId));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const createSurveillantGeneral = createAsyncThunk(
  'surveillant/create_surveillant_general',
  async (entity: ISurveillantGeneral, thunkAPI) => {
    const result = await axios.post<ISurveillantGeneral>(`${apiUrl}/general/${entity.ecoleId}`, cleanEntity(entity));
    thunkAPI.dispatch(getSurveillants(entity.ecoleId));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const createControleurBus = createAsyncThunk(
  'surveillant/create_surveillant_general',
  async (entity: ISurveillant, thunkAPI) => {
    const result = await axios.post<ISurveillant>(`${apiUrl}/controleur-bus/${entity.ecoleId}`, cleanEntity(entity));
    thunkAPI.dispatch(getSurveillants(entity.ecoleId));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const updateSurveillant = createAsyncThunk(
  'surveillant/update_surveillant',
  async (entity: ISurveillant, thunkAPI) => {
    const result = await axios.put<ISurveillant>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getSurveillants(entity.ecoleId));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const deleteSurveillant = createAsyncThunk(
  'surveillant/delete_surveillant',
  async (entity: ISurveillant, thunkAPI) => {
    const requestUrl = `${apiUrl}/${entity.id}`;
    const result = await axios.delete<ISurveillant>(requestUrl);
    thunkAPI.dispatch(getSurveillants(entity.ecoleId));
    return result;
  },
  { serializeError: serializeAxiosError }
);

// slice
export type SurveilantState = Readonly<typeof initialState>;
export const SurveillantSlice = createSlice({
  name: 'surveillant',
  initialState: initialState as SurveilantState,
  reducers: {
    reset() {
      return initialState;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getSurveillant.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(deleteSurveillant.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = {};
      })
      .addMatcher(isFulfilled(getSurveillants), (state, action) => {
        return {
          ...state,
          loading: false,
          entities: action.payload.data,
        };
      })
      .addMatcher(isFulfilled(createSurveillant, updateSurveillant), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(isFulfilled(createSurveillantGeneral), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entitySG = action.payload.data;
      })
      .addMatcher(isFulfilled(createControleurBus), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(isPending(getSurveillants, getSurveillant), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(
        isPending(createSurveillant, createSurveillantGeneral, updateSurveillant, deleteSurveillant, createControleurBus),
        state => {
          state.errorMessage = null;
          state.updateSuccess = false;
          state.updating = true;
        }
      );
  },
});

export const { reset } = SurveillantSlice.actions;

// Reducer
export default SurveillantSlice.reducer;
